/**
 * Component Showcase — dev only.
 * Served at /showcase; the route guard in app/showcase.tsx hard-redirects to
 * /+not-found in production builds, so this file is never reachable in prod.
 */

import React, { useRef, useState } from "react";
import { ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import type { ActionSheetRef } from "react-native-actions-sheet";

import { AppText } from "@/components/ui/AppText";
import { AppTextArea } from "@/components/ui/AppTextArea";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  ContentActions,
  APPROVE_ACTION_COLORS,
  REJECT_ACTION_COLORS,
  EDIT_ACTION_COLORS,
} from "@/components/ui/ContentActions";
import { FileUpload, type FileUploadState, type SelectedFile } from "@/components/ui/FileUpload";
import { ImageUpload, type ImageUploadState } from "@/components/ui/ImageUpload";
import { InfoCard } from "@/components/ui/InfoCard";
import { Modal } from "@/components/ui/Modal";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { CountryDialCodePicker } from "@/components/ui/CountryDialCodePicker";
import { CountryPicker } from "@/components/ui/CountryPicker";
import { SearchInput } from "@/components/ui/SearchInput";
import { SelectField, type SelectOption } from "@/components/ui/SelectField";
import { StepIndicator, type Step } from "@/components/ui/StepIndicator";
import { Badge } from "@/components/ui/Badge";
import { FilterGroup, type FilterOption } from "@/components/ui/FilterGroup";
import { Table, type TableColumn } from "@/components/ui/Table";
import { Tag } from "@/components/ui/Tag";
import { AppTextInput } from "@/components/ui/TextInput";
import { OtpInput } from "@/components/ui/OTPInput";
import { TabBarNavigation, type TabBarItem } from "@/components/ui/TabBarNavigation";
import { DropdownMenu } from "@/components/ui/DropdownMenu";

import { AppActionSheet } from "@/components/sheets/ActionSheet";
import { toast } from "@/lib/toast";

import type { TranslationKeys } from "@chinese-vendor/i18n";
import { Label } from "@/components/ui/Label";
import { Pill } from "@/components/ui/Pill";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { StepWizard } from "@/components/ui/StepWizard";

// ⚠️ REVIEWER NOTE: Showcase-only strings that aren't real i18n keys use
// `as TranslationKeys` casts — the `t()` fallback returns the string verbatim
// so the UI renders correctly. This route is unreachable in production builds.
type TK = TranslationKeys;

// ─── Section header helper ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View className="mb-8 w-full">
      <View className="mb-3 border-b border-stroke-20 pb-2">
        <AppText weight="bold" className="text-lg text-dark-text-50 uppercase tracking-wide">
          {title}
        </AppText>
      </View>
      <View className="gap-3 w-full">{children}</View>
    </View>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <View className="flex-row flex-wrap gap-2 items-center">{children}</View>;
}

// ─── Data fixtures ────────────────────────────────────────────────────────────

const STEPS: Step[] = [
  { label: "Create Account" as TK, description: "Completed" as TK, status: "completed" },
  { label: "Business Details" as TK, description: "In Progress" as TK, status: "current" },
  { label: "Upload Documents" as TK, description: "Pending" as TK, status: "pending" },
  { label: "Review & Submit" as TK, description: "Pending" as TK, status: "pending" },
];

const SELECT_OPTIONS: SelectOption[] = [
  { label: "Electronics", value: "electronics" },
  { label: "Apparel & Fashion", value: "apparel" },
  { label: "Home & Garden", value: "home" },
  { label: "Sports & Outdoors", value: "sports" },
  { label: "Health & Beauty", value: "health" },
];

type PortalTab = "overview" | "invitations" | "applications" | "adminUsers";

// ─── Showcase ─────────────────────────────────────────────────────────────────

export function ComponentShowcase() {
  // Local state for interactive components
  const [textValue, setTextValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [textAreaValue, setTextAreaValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [checkA, setCheckA] = useState(false);
  const [checkB, setCheckB] = useState(true);
  const [selectedOption, setSelectedOption] = useState<string | undefined>();
  const [modalVisible, setModalVisible] = useState(false);
  const [activePortalTab, setActivePortalTab] = useState<PortalTab>("overview");
  const [fileUploadState, setFileUploadState] = useState<FileUploadState>("idle");
  const [currentFile, setCurrentFile] = useState<SelectedFile | null>(null);
  const [imageUploadState, setImageUploadState] = useState<ImageUploadState>("idle");
  const [currentUris, setCurrentUris] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [phoneCountryPrimary, setPhoneCountryPrimary] = useState({
    flag: "🇸🇦",
    dialCode: "+966",
    name: "Saudi Arabia",
  });
  const [tablePage, setTablePage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("last24Hours");
  const [dropdownValue, setDropdownValue] = useState<"all" | "completed" | "pending">("all");
  const [dropdownSortValue, setDropdownSortValue] = useState<
    "alpha_asc" | "alpha_desc" | "last_edited"
  >("alpha_asc");

  const showcaseFilterOptions: FilterOption[] = [
    { id: "last24Hours", label: "Last 24 Hours" },
    { id: "last7Days", label: "Last 7 Days" },
    { id: "last30Days", label: "Last 30 Days" },
  ];

  const [phoneCountrySecondary, setPhoneCountrySecondary] = useState({
    flag: "🇨🇳",
    dialCode: "+86",
    name: "China",
  });
  const [showcaseCountry, setShowcaseCountry] = useState({
    flag: "🇸🇦",
    dialCode: "+966",
    name: "Saudi Arabia",
  });
  const actionSheetRef = useRef<ActionSheetRef>(null);

  const showcaseFile: SelectedFile = {
    uri: "https://example.com/document-upload.pdf",
    name: "Document upload.pdf",
    type: "application/pdf",
    size: 200 * 1024,
  };

  const showcaseImageUris = [
    "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=600&q=80",
  ];

  const portalTabs: TabBarItem<PortalTab>[] = [
    { key: "overview", label: "Overview" },
    { key: "invitations", label: "Invitations" },
    { key: "applications", label: "Applications" },
    { key: "adminUsers", label: "Admin Users" },
  ];

  return (
    <SafeAreaView className="flex-1 bg-white">
      <ScrollView
        className="flex-1"
        contentContainerClassName="px-page-x py-6 pb-[60px]"
        showsVerticalScrollIndicator={false}
      >
        <View className="size-full max-w-md mx-auto">
          {/* ── Title ─────────────────────────────────────────────────────────── */}
          <View className="mb-8 items-center">
            <AppText weight="bold" className="text-2xl text-dark-text-50">
              Component Showcase
            </AppText>
            <AppText weight="regular" className="text-sm text-supporting-text mt-1">
              Temporary review screen — all UI atoms
            </AppText>
          </View>

          {/* ── Button ────────────────────────────────────────────────────────── */}
          <Section title="Button">
            <Button label={"Primary" as TK} variant="primary" onPress={() => {}} />
            <Button label={"Secondary" as TK} variant="secondary" onPress={() => {}} />
            <Button label={"Outline" as TK} variant="outline" onPress={() => {}} />
            <Button label={"Tertiary" as TK} variant="tertiary" onPress={() => {}} />
            <Button label={"Ghost" as TK} variant="ghost" onPress={() => {}} />
            <Button label={"Destructive" as TK} variant="destructive" onPress={() => {}} />
            <Button label={"Text" as TK} variant="text" onPress={() => {}} />
            <Button label="common.loading" variant="primary" onPress={() => {}} loading />
            <Button label="common.disabled" variant="primary" onPress={() => {}} disabled />
            <Row>
              <Button
                label={"Leading icon" as TK}
                variant="primary"
                onPress={() => {}}
                icon={
                  <AppText weight="bold" className="text-white">
                    ⬆
                  </AppText>
                }
              />
              <Button
                label={"Trailing icon" as TK}
                variant="secondary"
                onPress={() => {}}
                trailingIcon={
                  <AppText weight="bold" className="text-primary">
                    →
                  </AppText>
                }
              />
              <Button
                variant="primary"
                onPress={() => {}}
                icon={
                  <AppText weight="bold" className="text-white text-lg">
                    +
                  </AppText>
                }
                iconOnly
                accessibilityLabel={"Add" as TK}
              />
            </Row>
          </Section>

          {/* ── ContentActions ────────────────────────────────────────────────── */}
          <Section title="ContentActions">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Outlined (default) — used on list rows before a decision is made.
            </AppText>
            <ContentActions
              actions={[
                {
                  label: "review.application.approve",
                  onPress: () => toast.success({ message: "Approved" }),
                  ...APPROVE_ACTION_COLORS,
                  testID: "showcase-approve",
                },
                {
                  label: "review.application.reject",
                  onPress: () => toast.error({ message: "Rejected" }),
                  ...REJECT_ACTION_COLORS,
                  testID: "showcase-reject",
                },
                {
                  label: "review.application.edit",
                  onPress: () => {},
                  ...EDIT_ACTION_COLORS,
                  testID: "showcase-edit",
                },
              ]}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-2">
              Colored — solid fill for committed decisions.
            </AppText>
            <ContentActions
              colored
              actions={[
                {
                  label: "review.application.approve",
                  onPress: () => toast.success({ message: "Approved" }),
                  ...APPROVE_ACTION_COLORS,
                },
                {
                  label: "review.application.reject",
                  onPress: () => toast.error({ message: "Rejected" }),
                  ...REJECT_ACTION_COLORS,
                },
                { label: "review.application.edit", onPress: () => {}, ...EDIT_ACTION_COLORS },
              ]}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-2">
              Disabled state.
            </AppText>
            <ContentActions
              disabled
              actions={[
                {
                  label: "review.application.approve",
                  onPress: () => {},
                  ...APPROVE_ACTION_COLORS,
                },
                { label: "review.application.reject", onPress: () => {}, ...REJECT_ACTION_COLORS },
              ]}
            />
          </Section>

          {/* ── Pill ─────────────────────────────────────────────── */}
          <Section title="Pill">
            <Row>
              <Pill label={"Contact" as TK} variant="contact" onPress={() => {}} />
              <Pill label={"Outline" as TK} variant="outline" onPress={() => {}} />
            </Row>
            <Row>
              <Pill plainLabel="Luxury Goods" variant="tag" />
              <Pill plainLabel="Fashion" variant="tag" />
              <Pill plainLabel="Automotive" variant="tag" />
            </Row>
          </Section>

          {/* ── Badge ─────────────────────────────────────────────── */}
          <Section title="Badge">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Filled variants (Status)
            </AppText>
            <Row>
              <Badge variant="filled-success" label="Success" />
              <Badge variant="filled-warning" label="Warning" />
              <Badge variant="filled-error" label="Error" />
              <Badge variant="default" label="Default" />
            </Row>
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-4">
              Accent variants
            </AppText>
            <Row>
              <Badge variant="accent-green" label="Accent Green" />
              <Badge variant="accent-blue" label="Accent Blue" />
              <Badge variant="accent-purple" label="Accent Purple" />
            </Row>
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-4">
              Rounded (pill) shape
            </AppText>
            <Row>
              <Badge variant="filled-success" shape="rounded" label="Approved" />
              <Badge variant="filled-warning" shape="rounded" label="Pending" />
              <Badge variant="filled-error" shape="rounded" label="Rejected" />
            </Row>
          </Section>

          {/* ── Table ─────────────────────────────────────────────── */}
          <Section title="Table">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Simple generic table structure with explicit column widths.
            </AppText>
            <Table
              minWidth={600}
              columns={
                [
                  {
                    key: "name",
                    header: "Name",
                    width: 200,
                    renderCell: (item) => <AppText>{item.name}</AppText>,
                  },
                  {
                    key: "role",
                    header: "Role",
                    width: 150,
                    renderCell: (item) => <AppText>{item.role}</AppText>,
                  },
                  {
                    key: "status",
                    header: "Status",
                    flex: 1,
                    renderCell: (item) => (
                      <Badge
                        variant={item.status === "Active" ? "filled-success" : "default"}
                        label={item.status}
                      />
                    ),
                  },
                ] as TableColumn<any>[]
              }
              data={[
                { id: "1", name: "Alice Smith", role: "Admin", status: "Active" },
                { id: "2", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "3", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "4", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "5", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "6", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "7", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "8", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "9", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "10", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "11", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "12", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "13", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "14", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "15", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "16", name: "Bob Jones", role: "Staff", status: "Offline" },
                { id: "17", name: "Bob Jones", role: "Staff", status: "Offline" },
              ].slice((tablePage - 1) * 10, tablePage * 10)}
              keyExtractor={(item) => item.id}
              totalCount={17}
              page={tablePage}
              onPageChange={setTablePage}
              onRowPress={(item) => toast.success({ message: `Pressed ${item.name}` })}
            />
          </Section>

          {/* ── FilterGroup ──────────────────────────────────────────────── */}
          <Section title="FilterGroup">
            <FilterGroup
              options={showcaseFilterOptions}
              value={activeFilter}
              onChange={setActiveFilter}
            />
          </Section>

          {/* ── DropdownMenu ──────────────────────────────────────────────── */}
          <Section title="DropdownMenu">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Single section — flat status filter (All / Completed / Pending).
            </AppText>
            <DropdownMenu
              sections={[
                {
                  title: "STATUS",
                  items: [
                    { value: "all", label: "All" },
                    { value: "completed", label: "Completed" },
                    { value: "pending", label: "Pending" },
                  ],
                },
              ]}
              value={dropdownValue}
              onSelect={setDropdownValue}
              panelWidth={176}
              trigger={
                <View className="items-start border border-stroke-50 rounded px-3 py-2">
                  <AppText weight="medium" className="text-sm text-dark-text-50">
                    {"Filter: " + dropdownValue}
                  </AppText>
                </View>
              }
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-4">
              Multi-section — sort options (Alphabetically + Date).
            </AppText>
            <DropdownMenu
              sections={[
                {
                  title: "ALPHABETICALLY",
                  items: [
                    { value: "alpha_asc", label: "Ascending A → Z" },
                    { value: "alpha_desc", label: "Descending Z → A" },
                  ],
                },
                {
                  title: "DATE",
                  items: [{ value: "last_edited", label: "Last Edited" }],
                },
              ]}
              value={dropdownSortValue}
              onSelect={setDropdownSortValue}
              panelWidth={208}
              trigger={
                <View className="items-start border border-stroke-50 rounded px-3 py-2">
                  <AppText weight="medium" className="text-sm text-dark-text-50">
                    {"Sort: " + dropdownSortValue}
                  </AppText>
                </View>
              }
            />
          </Section>

          {/* ── Tag ───────────────────────────────────────────────────────────── */}
          <Section title="Tag">
            <Row>
              <Tag label={"Electronics" as TK} variant="selected" onRemove={() => {}} />
              <Tag label={"Apparel" as TK} variant="selected" onRemove={() => {}} />
              <Tag label={"Health" as TK} variant="selected" onRemove={() => {}} />
            </Row>
            <Row>
              <Tag label={"Add category" as TK} variant="add" onPress={() => {}} />
              <Tag label={"Add product" as TK} variant="add" onPress={() => {}} />
            </Row>
          </Section>

          {/* ── TabBarNavigation ─────────────────────────────────────────────────── */}
          <Section title="TabBarNavigation">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Staff portal tab bar. Tap a tab to change the active selection.
            </AppText>
            <TabBarNavigation<PortalTab>
              tabs={portalTabs}
              activeTab={activePortalTab}
              onTabPress={setActivePortalTab}
              contentContainerClassName="px-page-x"
            />
          </Section>

          {/* ── Breadcrumb ────────────────────────────────────────────────────── */}
          <Section title="Breadcrumb">
            <Breadcrumb
              segments={[
                { labelKey: "review.dashboard.title", href: "/" },
                { labelText: "Acme Corporation Ltd." },
              ]}
            />
          </Section>

          {/* ── Checkbox ──────────────────────────────────────────────────────── */}
          <Section title="Checkbox">
            <Row>
              <Checkbox checked={checkA} onChange={setCheckA} label={"Unchecked (tap me)" as TK} />
            </Row>
            <Row>
              <Checkbox checked={checkB} onChange={setCheckB} label={"Checked by default" as TK} />
            </Row>
            <Row>
              <Checkbox checked onChange={() => {}} label={"Required field" as TK} required />
            </Row>
            <Row>
              <Checkbox
                checked={false}
                onChange={() => {}}
                label={"Disabled unchecked" as TK}
                disabled
              />
              <Checkbox checked onChange={() => {}} label={"Disabled checked" as TK} disabled />
            </Row>
          </Section>

          {/* ── Label ─────────────────────────────────────────────────────────── */}
          <Section title="Label">
            <AppText weight="regular" className="text-sm text-supporting-text">
              Default label.
            </AppText>
            <Label label={"Email Address" as TK} />
            <AppText weight="regular" className="text-sm text-supporting-text">
              Required label with asterisk.
            </AppText>
            <Label label={"Phone Number" as TK} required />
            <AppText weight="regular" className="text-sm text-supporting-text">
              With custom className (e.g., different text color).
            </AppText>
            <Label label={"Custom Label" as TK} className="text-primary" />
          </Section>

          {/* ── OtpInput ─────────────────────────────────────────────────────────── */}
          <Section title="OtpInput">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Default — empty (tap to focus).
            </AppText>
            <OtpInput value={""} onChangeText={() => {}} />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-2">
              Partially filled.
            </AppText>
            <OtpInput value={"123"} onChangeText={() => {}} />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-2">
              Fully filled.
            </AppText>
            <OtpInput value={"987654"} onChangeText={() => {}} />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-2">
              Error state.
            </AppText>
            <OtpInput value={"000000"} onChangeText={() => {}} hasError />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2 mt-2">
              Disabled.
            </AppText>
            <OtpInput value={"555555"} onChangeText={() => {}} editable={false} />
          </Section>

          {/* ── TextInput ─────────────────────────────────────────────────────── */}
          <Section title="TextInput">
            <AppTextInput
              label={"Email address" as TK}
              placeholder={"you@example.com" as TK}
              value={textValue}
              onChangeText={setTextValue}
              keyboardType="email-address"
            />
            <AppTextInput
              label={"Password" as TK}
              placeholder={"Enter password" as TK}
              value={passwordValue}
              onChangeText={setPasswordValue}
              secureTextEntry
            />
            <AppTextInput
              label={"With hint" as TK}
              placeholder={"Company name" as TK}
              value=""
              onChangeText={() => {}}
              hint={"Enter your registered business name" as TK}
            />
            <AppTextInput
              label={"With error" as TK}
              placeholder={"Tax ID" as TK}
              value="bad value"
              onChangeText={() => {}}
              error={"Invalid format — use XX-XXXXXXX" as TK}
            />
            <AppTextInput
              label={"Disabled" as TK}
              placeholder={"Cannot edit" as TK}
              value="Read only value"
              onChangeText={() => {}}
              editable={false}
            />
            <AppTextInput
              label={"Left text affix" as TK}
              placeholder={"example.com" as TK}
              value=""
              onChangeText={() => {}}
              leftAffix="https://"
            />
            <AppTextInput
              label={"Right text affix" as TK}
              placeholder={"0" as TK}
              value=""
              onChangeText={() => {}}
              rightAffix=".00"
              keyboardType="numeric"
            />
            <AppTextInput
              label={"Both affixes" as TK}
              placeholder={"0" as TK}
              value=""
              onChangeText={() => {}}
              leftAffix="$"
              rightAffix="USD"
              keyboardType="numeric"
            />
          </Section>

          {/* ── SelectField ───────────────────────────────────────────────────── */}
          <Section title="SelectField">
            <SelectField
              label={"Product category" as TK}
              options={SELECT_OPTIONS}
              value={selectedOption}
              onChange={setSelectedOption}
              placeholder={"Choose a category…" as TK}
              modalTitle={"Select Category" as TK}
              hint={"Pick the primary category for your products" as TK}
            />
            <SelectField
              label={"With error" as TK}
              options={SELECT_OPTIONS}
              value={undefined}
              onChange={() => {}}
              placeholder={"Choose…" as TK}
              error={"Selection is required" as TK}
            />
            <SelectField
              label={"Disabled" as TK}
              options={SELECT_OPTIONS}
              value={undefined}
              onChange={() => {}}
              placeholder={"Disabled" as TK}
              disabled
            />
          </Section>

          {/* ── SearchInput ───────────────────────────────────────────────────── */}
          <Section title="SearchInput">
            <SearchInput
              label={"Search country" as TK}
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder={"Search countries…" as TK}
            />
            <SearchInput
              label={"Disabled" as TK}
              value="Sample query"
              onChangeText={() => {}}
              placeholder={"Disabled search" as TK}
              editable={false}
            />
          </Section>

          {/* ── CountryDialCodePicker ─────────────────────────────────────────── */}
          <Section title="CountryDialCodePicker">
            <CountryDialCodePicker
              dialCode={showcaseCountry.dialCode}
              countryFlag={showcaseCountry.flag}
              onCountrySelect={(country) => {
                setShowcaseCountry(country);
              }}
            />
            <AppText weight="regular" className="text-sm text-supporting-text">
              Selected: {showcaseCountry.flag} {showcaseCountry.name} ({showcaseCountry.dialCode})
            </AppText>
          </Section>

          {/* ── CountryPicker ───────────────────────────────────────────────── */}
          <Section title="CountryPicker">
            <CountryPicker
              countryName={showcaseCountry.name}
              countryFlag={showcaseCountry.flag}
              onCountrySelect={(country) => {
                setShowcaseCountry(country);
              }}
            />
            <AppText weight="regular" className="text-sm text-supporting-text">
              Selected: {showcaseCountry.flag} {showcaseCountry.name}
            </AppText>
          </Section>

          {/* ── AppTextArea ───────────────────────────────────────────────────── */}
          <Section title="AppTextArea">
            <AppTextArea
              label={"Description" as TK}
              placeholder={"Enter a description…" as TK}
              value={textAreaValue}
              onChangeText={setTextAreaValue}
              hint={"Up to 500 characters" as TK}
            />
            <AppTextArea
              label={"Short note (3 lines)" as TK}
              placeholder={"Short note…" as TK}
              value=""
              onChangeText={() => {}}
              numberOfLines={3}
            />
            <AppTextArea
              label={"Error state" as TK}
              value=""
              onChangeText={() => {}}
              error={"This field is required" as TK}
            />
            <AppTextArea
              label={"Disabled" as TK}
              value="Cannot edit this"
              onChangeText={() => {}}
              editable={false}
            />
          </Section>

          {/* ── PhoneInput ────────────────────────────────────────────────────── */}
          <Section title="PhoneInput">
            <PhoneInput
              label={"Phone number (built-in country sheet)" as TK}
              value={phoneValue}
              onChangeText={setPhoneValue}
              placeholder={"5X XXX XXXX" as TK}
              dialCode={phoneCountryPrimary.dialCode}
              countryFlag={phoneCountryPrimary.flag}
              onCountrySelect={setPhoneCountryPrimary}
              hint={"Tap the flag to pick a country" as TK}
            />
            <PhoneInput
              label={"Phone number (callback on select)" as TK}
              value=""
              onChangeText={() => {}}
              placeholder={"5X XXX XXXX" as TK}
              dialCode={phoneCountrySecondary.dialCode}
              countryFlag={phoneCountrySecondary.flag}
              onCountrySelect={setPhoneCountrySecondary}
            />
            <AppText weight="regular" className="text-sm text-supporting-text">
              Selected 1: {phoneCountryPrimary.flag} {phoneCountryPrimary.name} (
              {phoneCountryPrimary.dialCode})
            </AppText>
            <AppText weight="regular" className="text-sm text-supporting-text">
              Selected 2: {phoneCountrySecondary.flag} {phoneCountrySecondary.name} (
              {phoneCountrySecondary.dialCode})
            </AppText>
            <PhoneInput
              label={"Error state" as TK}
              value=""
              onChangeText={() => {}}
              placeholder={"5X XXX XXXX" as TK}
              error={"Invalid phone number" as TK}
            />
            <PhoneInput
              label={"Disabled" as TK}
              value="500 000 001"
              onChangeText={() => {}}
              editable={false}
            />
          </Section>

          {/* ── FileUpload ────────────────────────────────────────────────────── */}
          <Section title="FileUpload">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Idle — click (or drag on web) to pick a file.
            </AppText>
            <FileUpload
              label={"Product document" as TK}
              accept={["application/pdf", "image/png", "image/jpeg"]}
              maxSizeMB={2}
              onFile={(file) => {
                setCurrentFile(file);
                setFileUploadState("success");
              }}
              uploadState={fileUploadState}
              currentFile={currentFile}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Uploaded/active state (Figma).
            </AppText>
            <FileUpload
              label={"Uploaded" as TK}
              onFile={() => {}}
              uploadState="success"
              currentFile={showcaseFile}
              onViewFile={() => {}}
              onRemoveFile={() => {}}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Uploading state (Figma).
            </AppText>
            <FileUpload
              label={"Uploading…" as TK}
              onFile={() => {}}
              uploadState="uploading"
              uploadProgress={65}
              currentFile={showcaseFile}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Upload failed — file card with error message.
            </AppText>
            <FileUpload
              label={"Upload failed" as TK}
              onFile={() => {}}
              uploadState="error"
              currentFile={showcaseFile}
              onRemoveFile={() => {}}
              error={"Upload failed. Please try again." as TK}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Field validation error (no file selected).
            </AppText>
            <FileUpload
              label={"With error" as TK}
              onFile={() => {}}
              uploadState="idle"
              error={"File too large. Maximum size is 2 MB." as TK}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Disabled.
            </AppText>
            <FileUpload label={"Disabled" as TK} onFile={() => {}} disabled />
          </Section>

          {/* ── ImageUpload ───────────────────────────────────────────────────── */}
          <Section title="ImageUpload">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Idle — click (or drag on web) to pick images.
            </AppText>
            <ImageUpload
              label={"Product images" as TK}
              maxImages={16}
              allowMultiple
              onImages={(uris) => {
                setCurrentUris(uris);
                setImageUploadState("success");
              }}
              uploadState={imageUploadState}
              currentUris={currentUris}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Filled state (Figma).
            </AppText>
            <ImageUpload
              label={"Filled" as TK}
              onImages={() => {}}
              uploadState="success"
              currentUris={showcaseImageUris}
              onRemoveImage={() => {}}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Uploading state.
            </AppText>
            <ImageUpload label={"Uploading…" as TK} onImages={() => {}} uploadState="uploading" />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Error state.
            </AppText>
            <ImageUpload
              label={"With error" as TK}
              onImages={() => {}}
              error={"Only PNG or JPG files are allowed." as TK}
            />
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Disabled.
            </AppText>
            <ImageUpload label={"Disabled" as TK} onImages={() => {}} disabled />
          </Section>

          {/* ── Card ──────────────────────────────────────────────────────────── */}
          <Section title="Card">
            <Card variant="default">
              <AppText weight="semibold" className="text-base text-dark-text-50 mb-1">
                Default card
              </AppText>
              <AppText weight="regular" className="text-sm text-supporting-text">
                Subtle border with a light drop shadow. Use for most content blocks.
              </AppText>
            </Card>
            <Card variant="elevated">
              <AppText weight="semibold" className="text-base text-dark-text-50 mb-1">
                Elevated card
              </AppText>
              <AppText weight="regular" className="text-sm text-supporting-text">
                Stronger shadow — useful for floated surfaces like action sheets.
              </AppText>
            </Card>
            <Card variant="outlined">
              <AppText weight="semibold" className="text-base text-dark-text-50 mb-1">
                Outlined card
              </AppText>
              <AppText weight="regular" className="text-sm text-supporting-text">
                Heavier border, no shadow — good for selectable items.
              </AppText>
            </Card>
            <Card
              variant="default"
              header={
                <AppText weight="bold" className="text-base text-dark-text-50">
                  Card with header & footer
                </AppText>
              }
              footer={<Button label={"Action" as TK} variant="ghost" onPress={() => {}} />}
              onPress={() => {}}
              accessibilityLabel={"Pressable card example" as TK}
            >
              <AppText weight="regular" className="text-sm text-supporting-text">
                Tap anywhere on this card — it is pressable.
              </AppText>
            </Card>
          </Section>

          {/* ── InfoCard ──────────────────────────────────────────────────────── */}
          <Section title="InfoCard">
            <InfoCard
              variant="secure"
              title="infoCard.secure.title"
              description="infoCard.secure.description"
            />
            <InfoCard
              variant="assist"
              title="infoCard.assist.title"
              description="infoCard.assist.description"
            />
            <InfoCard
              variant="approval"
              title="infoCard.approval.title"
              description="infoCard.approval.description"
            />
          </Section>

          {/* ── Vertical StepIndicator ─────────────────────────────────────────────────── */}
          <Section title="VerticalStepIndicator">
            <Card variant="default">
              <StepIndicator steps={STEPS} />
            </Card>
          </Section>

          {/* ── Horizontal StepIndicator ─────────────────────────────────────────────────── */}
          <Section title="HorizontalStepIndicator">
            <Card variant="default">
              <StepIndicator orientation="horizontal" steps={STEPS} />
            </Card>
          </Section>

          {/* ── StepWizard ────────────────────────────────────────────────────── */}
          <Section title="StepWizard">
            <AppText weight="regular" className="text-sm text-supporting-text">
              Horizontal — step 2 of 3 (default orientation).
            </AppText>
            <StepWizard step={2} labels={["Account" as TK, "Business" as TK, "Documents" as TK]} />
            <AppText weight="regular" className="text-sm text-supporting-text">
              Horizontal with children — content renders below the pills row.
            </AppText>
            <StepWizard step={2} labels={["Account" as TK, "Business" as TK, "Documents" as TK]}>
              <View className="bg-stroke-15 rounded-lg p-4">
                <AppText weight="medium" className="text-sm text-dark-text-50">
                  Step 2 content area
                </AppText>
                <AppText weight="regular" className="text-xs text-supporting-text mt-1">
                  Children render below the pills row.
                </AppText>
              </View>
            </StepWizard>
            <AppText weight="regular" className="text-sm text-supporting-text">
              Vertical — step 2 of 4.
            </AppText>
            <StepWizard
              orientation="vertical"
              step={2}
              labels={["Account" as TK, "Business" as TK, "Documents" as TK, "Review" as TK]}
            />
            <AppText weight="regular" className="text-sm text-supporting-text">
              Vertical with children — content fills the right column.
            </AppText>
            <StepWizard
              orientation="vertical"
              step={2}
              labels={["Account" as TK, "Business" as TK, "Documents" as TK, "Review" as TK]}
            >
              <View className="bg-stroke-15 rounded-lg p-4">
                <AppText weight="medium" className="text-sm text-dark-text-50">
                  Active step content
                </AppText>
                <AppText weight="regular" className="text-xs text-supporting-text mt-1">
                  Children render beside the step list.
                </AppText>
              </View>
            </StepWizard>
          </Section>

          {/* ── Toast ─────────────────────────────────────────────────────────── */}
          <Section title="Toast">
            <AppText weight="regular" className="text-sm text-supporting-text mb-2">
              Tap a button to fire the corresponding toast variant.
            </AppText>
            <Row>
              <Button
                label={"Success toast" as TK}
                variant="primary"
                onPress={() =>
                  toast.success({
                    message: "Profile saved successfully!",
                    title: "Saved",
                    duration: 3000,
                  })
                }
              />
              <Button
                label={"Error toast" as TK}
                variant="destructive"
                onPress={() =>
                  toast.error({
                    message: "Upload failed — please try again.",
                    title: "Error",
                    duration: 3000,
                  })
                }
              />
              <Button
                label={"Info toast" as TK}
                variant="secondary"
                onPress={() =>
                  toast.info({
                    message: "A new version of the app is available.",
                    duration: 3000,
                  })
                }
              />
              <Button
                label={"Persistent toast" as TK}
                variant="ghost"
                onPress={() =>
                  toast.success({
                    message: "This toast won't auto-dismiss. Tap it to close.",
                    duration: 0,
                  })
                }
              />
            </Row>
          </Section>

          {/* ── ProgressBar ──────────────────────────────────────────────────── */}
          <Section title="ProgressBar">
            <AppText weight="regular" className="text-sm text-supporting-text">
              Step 1 of 5.
            </AppText>
            <ProgressBar step={1} totalSteps={5} />
            <AppText weight="regular" className="text-sm text-supporting-text">
              Step 3 of 5.
            </AppText>
            <ProgressBar step={3} totalSteps={5} />
            <AppText weight="regular" className="text-sm text-supporting-text">
              All complete (step 5 of 5).
            </AppText>
            <ProgressBar step={5} totalSteps={5} />
            <AppText weight="regular" className="text-sm text-supporting-text">
              Not started (step 0 of 4).
            </AppText>
            <ProgressBar step={0} totalSteps={4} />
          </Section>

          {/* ── AppActionSheet ───────────────────────────────────────────────── */}
          <Section title="AppActionSheet">
            <Button
              label={"Open reusable action sheet" as TK}
              variant="secondary"
              onPress={() => actionSheetRef.current?.show()}
            />
            <AppActionSheet
              ref={actionSheetRef}
              headerTitle={"Reusable Action Sheet" as TK}
              footer={
                <View className="px-4 pb-4">
                  <Button
                    label="common.close"
                    variant="secondary"
                    onPress={() => actionSheetRef.current?.hide()}
                  />
                </View>
              }
            >
              <View className="px-4 pb-3">
                <AppText weight="regular" className="text-sm text-dark-text-20">
                  This demonstrates the shared sheet wrapper with default styles and optional header
                  / footer props.
                </AppText>
              </View>
            </AppActionSheet>
          </Section>

          {/* ── Modal ─────────────────────────────────────────────────────────── */}
          <Section title="Modal">
            <Button
              label={"Open modal" as TK}
              variant="secondary"
              onPress={() => setModalVisible(true)}
            />
          </Section>
        </View>
      </ScrollView>

      {/* ── Modal overlay ─────────────────────────────────────────────────── */}
      <Modal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
        title="auth.login.title"
        footer={
          <View className="gap-2">
            <Button
              label="common.confirm"
              variant="primary"
              onPress={() => setModalVisible(false)}
            />
            <Button
              label="common.cancel"
              variant="secondary"
              onPress={() => setModalVisible(false)}
            />
          </View>
        }
      >
        <AppText weight="regular" className="text-sm text-supporting-text">
          This is the modal body. It can contain any content — forms, confirmations, pickers, etc.
        </AppText>
        <View className="mt-4">
          <Checkbox
            checked={checkA}
            onChange={setCheckA}
            label={"I agree to the terms and conditions" as TK}
            required
          />
        </View>
      </Modal>
    </SafeAreaView>
  );
}
